/**
 * Leave blackout periods, minimum-staffing rules and the month grid that shows them (ADR 0032,
 * Phase 4c).
 *
 * A BLACKOUT PERIOD says "no leave may be requested for days inside this window" for everyone, a
 * department or a location, with a reason. A MINIMUM-STAFFING RULE says "on any working day at
 * least N people (or P percent) of this population must be present". Both are checks on a request
 * — enforced by the API, not here — and both are only configuration until that wiring lands, so
 * with no row of either kind every request behaves exactly as before.
 *
 * SCOPE is `everyone | department | location`: these are operational limits on a group of people
 * who work together, so unlike a leave plan there is no grade or employment-type target.
 *
 * EFFECTIVE-DATED (invariant 5). A blackout's WINDOW is `fromDate`..`toDate`; the RECORD has its own
 * `validFrom` / `validTo`. Creating a blackout opens the record today (in the organisation's terms);
 * deleting it never removes the row — it closes the record (`validTo` set), so "was leave blocked
 * on 12 March, and why" stays answerable, and a blackout that has been lifted stops blocking new
 * requests. A staffing rule is set like a plan rule: the open row for the same target closes the day
 * before `validFrom` and a new one opens (ONE open row per target); `active: false` ends it.
 *
 * STAFFING SEMANTICS (every implementation must agree):
 *   - The population of a rule is the people employed in its scope on the day: everyone in the
 *     organisation, the department, or the location (not its sub-departments).
 *   - Only people SCHEDULED to work that day count (not on a weekly off, not on a holiday of their
 *     location); `presentCount = scheduledCount - approvedLeaveCount`. Pending requests do not
 *     reduce presence — they are shown beside it.
 *   - A rule is breached on a day when `presentCount` < the required count, where the required
 *     count is `minPresentCount`, or `ceil(minPresentPercent / 100 * scheduledCount)`.
 *   - Rules do not override each other: every rule whose population contains the person must hold.
 *     Days with nobody scheduled (a holiday for the whole population) are never breached.
 *
 * RN-SAFE: zod only, no Node built-ins.
 */

import { z } from 'zod';
import {
  dateOnlySchema,
  instantSchema,
  orgIdSchema,
  uuidSchema,
} from './schemas/common.js';

// ---------------------------------------------------------------------------
// Scope
// ---------------------------------------------------------------------------

export const LEAVE_RESTRICTION_SCOPES = ['everyone', 'department', 'location'] as const;
export const leaveRestrictionScopeSchema = z.enum(LEAVE_RESTRICTION_SCOPES);
export type LeaveRestrictionScope = z.infer<typeof leaveRestrictionScopeSchema>;

/** The shape rule shared by the request bodies and the database CHECKs: a department or location
 * names its row, everyone names nothing. Returns the message of the violation, or null. */
export function leaveRestrictionScopeProblem(
  scope: LeaveRestrictionScope,
  scopeValueId: string | null,
): string | null {
  if (scope === 'everyone') {
    return scopeValueId === null ? null : 'The everyone scope takes no scopeValueId';
  }
  return scopeValueId === null ? 'This scope needs the id of the department or location' : null;
}

/** The longest window one blackout may cover. A year-long freeze is a policy, not a blackout. */
export const LEAVE_BLACKOUT_MAX_DAYS = 366;

function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

// ---------------------------------------------------------------------------
// Blackout periods
// ---------------------------------------------------------------------------

export const leaveBlackoutPeriodSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  scope: leaveRestrictionScopeSchema,
  scopeValueId: uuidSchema.nullable(),
  /** The department or location name, for display; null for everyone. */
  scopeLabel: z.string().nullable(),
  /** The first and last day leave is blocked, inclusive. */
  fromDate: dateOnlySchema,
  toDate: dateOnlySchema,
  reason: z.string(),
  /** When the record took effect. */
  validFrom: dateOnlySchema,
  /** Null while the blackout is in force; the day it was lifted otherwise. */
  validTo: dateOnlySchema.nullable(),
  createdAt: instantSchema,
});
export type LeaveBlackoutPeriod = z.infer<typeof leaveBlackoutPeriodSchema>;

export const listLeaveBlackoutPeriodsQuerySchema = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  scope: leaveRestrictionScopeSchema.optional(),
  scopeValueId: uuidSchema.optional(),
  /** Only blackouts whose window overlaps [from, to]. Both or neither. */
  from: dateOnlySchema.optional(),
  to: dateOnlySchema.optional(),
  /** True also lists lifted blackouts. Default: only those in force. */
  history: z
    .enum(['true', 'false'])
    .transform((value) => value === 'true')
    .optional(),
});

export const createLeaveBlackoutPeriodBodySchema = z
  .object({
    scope: leaveRestrictionScopeSchema,
    scopeValueId: uuidSchema.nullable().default(null),
    fromDate: dateOnlySchema,
    toDate: dateOnlySchema,
    /** Shown to the employee whose request is refused, so say it plainly. */
    reason: z.string().trim().min(1).max(300),
  })
  .strict()
  .superRefine((value, ctx) => {
    const problem = leaveRestrictionScopeProblem(value.scope, value.scopeValueId);
    if (problem) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['scopeValueId'], message: problem });
    }
    if (value.toDate < value.fromDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['toDate'],
        message: 'toDate must be on or after fromDate',
      });
    } else if (daysBetween(value.fromDate, value.toDate) + 1 > LEAVE_BLACKOUT_MAX_DAYS) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['toDate'],
        message: `A blackout cannot cover more than ${LEAVE_BLACKOUT_MAX_DAYS} days`,
      });
    }
  });
export type CreateLeaveBlackoutPeriodBody = z.infer<typeof createLeaveBlackoutPeriodBodySchema>;

/** The blackout that was lifted, with `validTo` set. */
export const endLeaveBlackoutPeriodResultSchema = z.object({ blackout: leaveBlackoutPeriodSchema });

// ---------------------------------------------------------------------------
// Minimum staffing
// ---------------------------------------------------------------------------

export const LEAVE_MIN_STAFFING_MAX_COUNT = 100_000;

export const leaveMinStaffingRuleSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  scope: leaveRestrictionScopeSchema,
  scopeValueId: uuidSchema.nullable(),
  scopeLabel: z.string().nullable(),
  /** Exactly one of the two is set. */
  minPresentCount: z.number().int().min(1).max(LEAVE_MIN_STAFFING_MAX_COUNT).nullable(),
  minPresentPercent: z.number().int().min(1).max(100).nullable(),
  validFrom: dateOnlySchema,
  /** Null on the open row. */
  validTo: dateOnlySchema.nullable(),
  createdAt: instantSchema,
});
export type LeaveMinStaffingRule = z.infer<typeof leaveMinStaffingRuleSchema>;

export const listLeaveMinStaffingRulesQuerySchema = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  scope: leaveRestrictionScopeSchema.optional(),
  /** The date to list rules in force on; absent = today in the organisation's terms. */
  asOf: dateOnlySchema.optional(),
  /** True lists every row, closed ones included, and ignores `asOf`. */
  history: z
    .enum(['true', 'false'])
    .transform((value) => value === 'true')
    .optional(),
});

/**
 * Set the minimum staffing for a target from a date, or stop requiring it (`active: false`, which
 * closes the open row and opens nothing). With `active: true` exactly one of the two minimums is
 * required; with `active: false` neither may be sent.
 */
export const setLeaveMinStaffingRuleBodySchema = z
  .object({
    scope: leaveRestrictionScopeSchema,
    scopeValueId: uuidSchema.nullable().default(null),
    minPresentCount: z.number().int().min(1).max(LEAVE_MIN_STAFFING_MAX_COUNT).nullable().default(null),
    minPresentPercent: z.number().int().min(1).max(100).nullable().default(null),
    /** When this version takes effect. A date at or before the open row's own is refused. */
    validFrom: dateOnlySchema,
    active: z.boolean().default(true),
  })
  .strict()
  .superRefine((value, ctx) => {
    const problem = leaveRestrictionScopeProblem(value.scope, value.scopeValueId);
    if (problem) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['scopeValueId'], message: problem });
    }
    const given = [value.minPresentCount, value.minPresentPercent].filter((v) => v !== null).length;
    if (value.active && given !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['minPresentCount'],
        message: 'Set exactly one of minPresentCount and minPresentPercent',
      });
    }
    if (!value.active && given !== 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['minPresentCount'],
        message: 'Ending a rule takes no minimum',
      });
    }
  });
export type SetLeaveMinStaffingRuleBody = z.infer<typeof setLeaveMinStaffingRuleBodySchema>;

/** The row that opened, or null when the call only ended one. */
export const setLeaveMinStaffingRuleResultSchema = z.object({
  rule: leaveMinStaffingRuleSchema.nullable(),
});

/** Required people for a rule on a day with `scheduledCount` people scheduled (see the header). */
export function leaveMinStaffingRequired(
  rule: Pick<LeaveMinStaffingRule, 'minPresentCount' | 'minPresentPercent'>,
  scheduledCount: number,
): number {
  if (rule.minPresentCount !== null) return rule.minPresentCount;
  if (rule.minPresentPercent !== null) return Math.ceil((rule.minPresentPercent * scheduledCount) / 100);
  return 0;
}

// ---------------------------------------------------------------------------
// Month grid
// ---------------------------------------------------------------------------

export const leaveMonthSchema = z
  .string()
  .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Must be YYYY-MM');

export const getLeaveMonthCalendarQuerySchema = z.object({
  /** The month to show, e.g. "2026-12". */
  month: leaveMonthSchema,
  /** Narrow to one department / location. With neither, the whole organisation. */
  departmentId: uuidSchema.optional(),
  locationId: uuidSchema.optional(),
});

/** One staffing rule as it stands on one day. */
export const leaveMonthStaffingSchema = z.object({
  ruleId: uuidSchema,
  scope: leaveRestrictionScopeSchema,
  scopeValueId: uuidSchema.nullable(),
  scopeLabel: z.string().nullable(),
  scheduledCount: z.number().int().nonnegative(),
  presentCount: z.number().int().nonnegative(),
  requiredCount: z.number().int().nonnegative(),
  breached: z.boolean(),
});

export const leaveMonthDaySchema = z.object({
  date: dateOnlySchema,
  /** One entry per distinct holiday on the date across the locations in view. */
  holidays: z.array(
    z.object({ name: z.string(), isOptional: z.boolean(), locationIds: z.array(uuidSchema) }),
  ),
  /** People in view scheduled to work (not on a weekly off or a holiday). */
  scheduledCount: z.number().int().nonnegative(),
  /** Approved leave, counted over the people in view. Counts only — no names, no leave types. */
  approvedLeaveCount: z.number().int().nonnegative(),
  pendingLeaveCount: z.number().int().nonnegative(),
  /** Blackouts in force that cover the date and touch the view. */
  blackouts: z.array(z.object({ id: uuidSchema, reason: z.string() })),
  isBlackout: z.boolean(),
  /** Every staffing rule that touches the view, evaluated on its OWN population (see the header). */
  staffing: z.array(leaveMonthStaffingSchema),
  /** True when any entry of `staffing` is breached. */
  staffingBreach: z.boolean(),
});
export type LeaveMonthDay = z.infer<typeof leaveMonthDaySchema>;

export const leaveMonthCalendarSchema = z.object({
  month: leaveMonthSchema,
  departmentId: uuidSchema.nullable(),
  locationId: uuidSchema.nullable(),
  /** Every day of the month, in order. */
  days: z.array(leaveMonthDaySchema),
});
export type LeaveMonthCalendar = z.infer<typeof leaveMonthCalendarSchema>;
